import type { AddMembersResult, GroupAccessItem, GroupColor, GroupIcon, GroupRelation } from '@ralph/contracts'
import type { OptionMark, ReachLine, ReviewLine, SectionKey } from '@/components/groups/audience-picker.ts'
import type { GroupIconRow } from '@/components/groups/group-icons.ts'
import { pluralCategory } from '@/i18n/format.ts'
import type { I18n } from '@/i18n/I18nProvider.tsx'

/**
 * `groups` — every sentence the groups plane shows (AGM-124: copy lives in catalogues, never in
 * components).
 *
 * The vocabulary is PRD-26's (D-37) and it is load-bearing: "group", never "team"; "Who can use
 * it"; "everyone who signs in", never "the whole company"; "Send for review", "Waiting for
 * review", "sharing request"; "Agents that use it" and "Let it answer for everyone it reaches",
 * never "lend". A person reading two screens must meet one set of words for one set of ideas.
 *
 * English is the source of truth AND the type (PRD-27 LOC-011), so the Portuguese sibling is
 * checked against the shape of this file and a missing key is a compile error.
 *
 * Two things that used to live beside the components are here now because they are words:
 * the 48 icon names and the 6 row headings of the icon chooser — a catalogue nothing extracted
 * and nobody could see, since they render as `aria-label` and `title` (F-15) — and the six
 * colour names. What stayed behind in `group-icons.ts` is the glyph map and the row structure,
 * which are not words and must not be able to differ per locale (LOC-017).
 */

/**
 * What a sentence here needs from the reader's language (PRD-27 LOC-016, LOC-020).
 *
 * Passed in, never imported: a catalogue is data, and one that reached for the locale itself
 * would capture it at import and freeze the language on a switch — with no type error, no
 * runtime error and no failing test (F-3). The component that renders the sentence has
 * `useI18n()` in its body and hands the bag down.
 */
export type GroupFmt = Pick<I18n, 'locale' | 'formatCount' | 'formatList'>

/**
 * Which of an entry's own plural forms to say.
 *
 * Never `count === 1`. Brazilian Portuguese puts ZERO in the `one` category — "0 pessoa", not
 * "0 pessoas" — so a two-branch comparison is already wrong in one of the two shipped
 * languages, on every empty state, and nothing fails when it is.
 */
const pick = (fmt: GroupFmt, count: number, forms: { one: string; other: string }): string =>
  pluralCategory(fmt.locale, count) === 'one' ? forms.one : forms.other

/** The common shape: the count with the locale's separator, then its noun. */
const counted = (fmt: GroupFmt, count: number, forms: { one: string; other: string }): string =>
  `${fmt.formatCount(count)} ${pick(fmt, count, forms)}`

const people = (fmt: GroupFmt, count: number): string => counted(fmt, count, { one: 'person', other: 'people' })

export const groups = {
  // ---------------------------------------------------------------------------
  // The picker: "Who can use it"
  // ---------------------------------------------------------------------------
  picker: {
    legend: 'Who can use it',
    kinds: {
      private: { label: (privateLabel: string): string => privateLabel, hint: 'Only the people who maintain it' },
      groups: { label: 'Specific groups', hint: 'The people in the groups you choose' },
      everyone: { label: 'Everyone', hint: 'Everyone who signs in' }
    },
    systemKinds: {
      everyone: { label: 'Everyone', hint: 'Everyone who signs in' },
      groups: { label: 'Specific groups', hint: 'Only people in the groups you choose' }
    },
    searchLabel: 'Find a group',
    searchPlaceholder: 'Search groups by name',
    chosenLabel: 'Chosen groups',
    sections: {
      manage: 'You manage',
      member: 'Includes you',
      other: 'Other groups'
    } satisfies Record<SectionKey, string>,
    /** "Other groups" when it is the only section: other than nothing. */
    allGroups: 'All groups',
    optionMeta: (fmt: GroupFmt, count: number, manage: boolean, member: boolean): string =>
      [people(fmt, count), manage ? 'you manage' : null, member ? 'includes you' : null].filter(Boolean).join(' · '),
    /** Nine-pixel uppercase chips: the shortest idiomatic form wins over the most literal one. */
    marks: {
      shared_now: 'shared now',
      needs_review: 'needs review'
    } satisfies Record<OptionMark, string>,
    remove: (name: string): string => `Remove ${name}`,
    results: (fmt: GroupFmt, count: number): string => counted(fmt, count, { one: 'group', other: 'groups' }),
    noMatch: (q: string): string => `No group is called “${q}”.`,
    noGroups: 'There are no groups yet. An administrator creates them on the Groups page.',
    loading: 'Loading groups…',
    failed: 'Groups could not be loaded. Try again in a moment.',
    showList: 'Show groups',
    hideList: 'Hide groups'
  },

  reach: (fmt: GroupFmt, line: ReachLine, labels: { privateLabel: string; consequence: string }): string => {
    switch (line.kind) {
      case 'pending':
        return 'Counting who this reaches…'
      case 'no_groups':
        return 'Pick at least one group.'
      case 'private':
        return line.people === null ? labels.privateLabel : `${labels.privateLabel} · ${people(fmt, line.people)}`
      case 'everyone':
        return line.people === null
          ? 'Reaches everyone who signs in.'
          : `Reaches everyone who signs in — ${people(fmt, line.people)} today.`
      case 'groups': {
        if (line.people === null) return 'Counting who this reaches…'
        const base = `Reaches ${people(fmt, line.people)}`
        if (line.delta === null) return `${base}.`
        if (line.delta === 0) return `${base} — the same as now.`
        if (line.delta > 0) return `${base} — ${fmt.formatCount(line.delta)} more than now.`
        return `${base} — ${fmt.formatCount(Math.abs(line.delta))} fewer than now. ${labels.consequence}`
      }
    }
  },

  review: (fmt: GroupFmt, line: ReviewLine, verb: 'publish' | 'save'): string | null => {
    const when = verb === 'publish' ? 'when you publish' : 'when you save'
    switch (line.kind) {
      case 'none':
        return null
      case 'administrator':
        return `You are an administrator, so this goes live ${when}.`
      case 'live_now':
        return line.managedAdded.length > 0
          ? `This goes live ${when} — you manage ${fmt.formatList(line.managedAdded)}.`
          : `This goes live ${when}.`
      case 'everyone':
        return 'Sharing with everyone needs an administrator’s review before it goes live.'
      case 'groups': {
        const waiting = pick(fmt, line.waiting.length, {
          one: `${line.waiting[0]} is not a group you manage, so an administrator will review adding it.`,
          other: `${fmt.formatList(line.waiting)} are not groups you manage, so an administrator will review adding them.`
        })
        // The removal fragment has a sentence-initial form of its own rather than being
        // capitalised after the fact: re-casing a built phrase assumes an orthography, and here
        // it would also capitalise a group NAME that the language may not want touched
        // (PRD-27 LOC-014). Only the first item of the list starts the sentence.
        const leads = line.nowAdded.length === 0
        const removals = line.nowRemoved.map((name, index) =>
          leads && index === 0 ? `Removing ${name}` : `removing ${name}`
        )
        const now = [...line.nowAdded, ...removals]
        return now.length === 0
          ? waiting
          : `${waiting} ${fmt.formatList(now)} ${pick(fmt, now.length, { one: 'goes', other: 'go' })} live now.`
      }
    }
  },

  /** What losing access costs, per kind of item, for the reach line's "fewer than now". */
  consequence: {
    agent: 'They lose it at their next message; their conversations stay readable.',
    knowledge_base: 'They lose it at their next search or message.',
    system: 'Their agents stop using it at their next message.'
  },

  /** Who "only its maintainers" is, in words (D-37: a private label names the people). */
  privateLabel: {
    me: 'Only me',
    groupManagers: (group: string): string => `Only ${group}’s managers`
  },

  // ---------------------------------------------------------------------------
  // The Groups page
  // ---------------------------------------------------------------------------
  page: {
    title: 'Groups',
    lead: 'People who work together. Share agents and knowledge with a group, and everyone in it can use them from their next request.',
    leadAdmin:
      'Create groups, appoint who manages each one, and see what every group gives access to. Managers add and remove the members.',
    newGroup: 'New group',
    searchLabel: 'Search groups',
    searchPlaceholder: 'Search by name',
    clearSearch: 'Clear the search',
    filters: { all: 'All groups', mine: 'Your groups', unmanaged: 'No managers' },
    adminFilters: { all: 'All', mine: 'Yours', unmanaged: 'No managers' },
    loading: 'Loading groups…',
    failed: 'Groups could not be loaded.',
    failedBody: 'Try again in a moment.',
    empty: 'There are no groups yet.',
    emptyBody: 'An administrator creates groups and appoints who manages them.',
    emptyAdmin: 'Create the first group, then appoint its managers — they add the members.',
    emptyMine: 'You are not in any group yet.',
    emptyMineBody: 'A group’s managers add people. The group pages say who manages each one.',
    noMatch: (q: string): string => `No group is called “${q}”.`,
    noMatchBody: 'Check the spelling, or look through all groups.',
    noManagersList: 'No managers',
    managedBy: (fmt: GroupFmt, names: string[]): string => `Managed by ${fmt.formatList(names)}`,
    rowMeta: (fmt: GroupFmt, count: number, manage: boolean, member: boolean): string =>
      [people(fmt, count), manage ? 'you manage' : null, member ? 'includes you' : null].filter(Boolean).join(' · '),
    pickOne: 'Choose a group to see who is in it and what it gives access to.',
    back: 'All groups'
  },

  detail: {
    loading: 'Loading the group…',
    missing: 'This group no longer exists.',
    missingBody: 'It may have been deleted. The list shows the groups there are now.',
    failed: 'This group could not be loaded.',
    failedBody: 'That is a problem reading it, not a group with nothing in it. Try again in a moment.',
    retry: 'Try again',
    membersFailed: 'Who is in it could not be loaded. Try again in a moment.',
    members: (fmt: GroupFmt, count: number): string => people(fmt, count),
    edit: 'Edit',
    delete: 'Delete group…',
    toJoin: (fmt: GroupFmt, names: string[]): string => `To join, ask ${fmt.formatList(names)}.`,
    nobodyManages:
      'Nobody manages this group yet, so only an administrator can add people to it, and administrators maintain what it owns. An administrator can appoint a manager.',
    sharedWith: (group: string): string => `Shared with ${group}`,
    sharedWithLead: (group: string): string => `What people in ${group} can use because they are in it.`,
    sharedWithEmpty: (group: string): string => `Nothing is shared with ${group} yet.`,
    ownedBy: (group: string): string => `Owned by ${group}`,
    ownedByLead: (group: string): string =>
      `Maintained by ${group}’s managers. Being in the group does not give its members these on its own — only sharing does.`,
    ownedByEmpty: (group: string): string => `${group} does not own anything yet.`,
    /**
     * `satisfies` rather than a bare object: a fifth kind of shared thing is then a compile
     * error here rather than a blank cell on a group's page (PRD-27 LOC-018).
     */
    kinds: {
      knowledge_base: 'Knowledge base',
      agent: 'Agent',
      connected_system: 'Connected system',
      remote_agent: 'Remote agent'
    } satisfies Record<GroupAccessItem['kind'], string>,
    /**
     * Who can use one of the things a group owns or is shared with.
     *
     * One function over the whole row rather than four labels looked up by `reach`, because the
     * row is polymorphic: a *knowledge base* is feminine in Portuguese and an *agent*, a
     * *connected system* and a *remote agent* are masculine, so "Shared with 3 groups" has two
     * correct translations and the one that decides between them is the kind. The kind is right
     * there on the item, so it is passed rather than designed away (PRD-27 D-21). English reads
     * exactly as it did.
     */
    reach: (fmt: GroupFmt, item: Pick<GroupAccessItem, 'kind' | 'reach' | 'groupCount'>): string => {
      switch (item.reach) {
        case 'private':
          return 'Only its maintainers'
        case 'groups':
          return `Shared with ${counted(fmt, item.groupCount, { one: 'group', other: 'groups' })}`
        case 'everyone':
          return 'Everyone'
        case 'nobody':
          return 'Nobody can use it'
      }
    },
    cannotOpen: 'You cannot open this',
    managers: 'Managers',
    managersLead: 'They add and remove members, and maintain what the group owns. Administrators appoint them.',
    noManagers: 'No managers yet.',
    appoint: 'Appoint a manager',
    removeManager: (name: string): string => `Stop ${name} managing this group`,
    managerNotMember: 'not a member',
    members_: 'Members',
    membersLead: 'Everyone here gets what is shared with the group, from their next request.',
    membersHidden: 'Only the people in this group, its managers and administrators see who is in it.',
    /** Operators and auditors outside the group: membership is access, so the People page shows it (D-63). */
    membersOnPeople: 'You can see who is in it, with their roles, on the People page.',
    /** People-plane readers (administrators, operators, auditors): the same members with their roles. */
    openInPeople: 'Roles and access',
    openInPeopleLabel: (group: string): string => `See everyone in ${group} with their roles, on the People page`,
    addPeople: 'Add people',
    searchMembers: 'Search members',
    searchMembersPlaceholder: 'Search by name or email',
    noMembers: 'Nobody is in this group yet.',
    noMemberMatch: (q: string): string => `Nobody in this group matches “${q}”.`,
    loadMore: 'Show more',
    removeMember: (name: string): string => `Remove ${name}`,
    manages: 'manager',
    addedBy: (name: string | null, when: string): string => (name ? `Added by ${name} · ${when}` : `Added ${when}`),
    you: 'you'
  },

  create: {
    title: 'New group',
    lead: 'A group is people who work together. Its managers add the members; you choose the managers.',
    name: 'Name',
    namePlaceholder: 'Finance EMEA',
    nameHint: 'Up to 60 characters. People see this wherever they share.',
    icon: 'Icon',
    color: 'Colour',
    managers: 'Managers (optional)',
    managersHint: 'They add and remove members. You can appoint managers later, too.',
    preview: 'Preview',
    previewMembers: 'No members yet',
    submit: 'Create group',
    submitting: 'Creating…',
    cancel: 'Cancel',
    taken: 'There is already a group with that name.'
  },

  edit: {
    title: (group: string): string => `Edit ${group}`,
    lead: 'Renaming a group never changes who can use what — everything is shared with the group, not its name.',
    submit: 'Save',
    submitting: 'Saving…',
    stale: 'Someone else changed this group while you were editing. Close this and open it again to see their change.'
  },

  /** Every swatch has a name, because a colour alone is not a label (PRD-26 D-6). */
  colors: {
    slate: 'Slate',
    teal: 'Teal',
    plum: 'Plum',
    moss: 'Moss',
    navy: 'Navy',
    clay: 'Clay'
  } satisfies Record<GroupColor, string>,

  /**
   * The 48 icon names, which are what a screen reader hears and what a `title` shows — never an
   * identifier (PRD-26 D-6, PRD-27 F-15). The set and its order belong to the contract; only the
   * words are here.
   */
  icons: {
    'users-round': 'People',
    handshake: 'Handshake',
    'heart-handshake': 'Care',
    'hand-heart': 'Support',
    network: 'Network',
    puzzle: 'Puzzle',
    trophy: 'Trophy',
    compass: 'Compass',
    'briefcase-business': 'Briefcase',
    landmark: 'Institution',
    scale: 'Scales',
    calculator: 'Calculator',
    banknote: 'Banknote',
    'piggy-bank': 'Savings',
    receipt: 'Receipt',
    'chart-line': 'Chart',
    megaphone: 'Megaphone',
    newspaper: 'Newspaper',
    mail: 'Mail',
    headset: 'Headset',
    'life-buoy': 'Lifebuoy',
    store: 'Shop',
    'shopping-cart': 'Cart',
    presentation: 'Presentation',
    'code-xml': 'Code',
    server: 'Server',
    cloud: 'Cloud',
    cpu: 'Chip',
    microscope: 'Microscope',
    atom: 'Atom',
    dna: 'DNA',
    rocket: 'Rocket',
    factory: 'Factory',
    'hard-hat': 'Hard hat',
    truck: 'Truck',
    package: 'Package',
    warehouse: 'Warehouse',
    building: 'Building',
    'map-pin': 'Location',
    plane: 'Plane',
    'graduation-cap': 'Graduation',
    school: 'School',
    stethoscope: 'Stethoscope',
    'heart-pulse': 'Health',
    leaf: 'Leaf',
    sprout: 'Sprout',
    palette: 'Palette',
    coffee: 'Coffee'
  } satisfies Record<GroupIcon, string>,

  /** What each row of eight in the chooser is: six categories, named. */
  iconRows: {
    groups: 'Groups',
    business: 'Business',
    customers: 'Customers',
    technology: 'Technology',
    operations: 'Operations',
    people: 'People and culture'
  } satisfies Record<GroupIconRow, string>,

  appoint: {
    title: (group: string): string => `Appoint a manager of ${group}`,
    lead: (group: string): string =>
      `Managers add and remove the people in ${group}. They can share their own agents and knowledge with it without an administrator’s review, and they maintain everything ${group} owns. Managing a group does not put them in it.`,
    person: 'Person',
    submit: 'Appoint',
    submitting: 'Appointing…'
  },

  add: {
    title: (group: string): string => `Add people to ${group}`,
    lead: (fmt: GroupFmt, group: string, bases: number, agents: number): string =>
      `They get what ${group} gives access to — ${counted(fmt, bases, { one: 'knowledge base', other: 'knowledge bases' })} and ${counted(fmt, agents, { one: 'agent', other: 'agents' })} — from their next request.`,
    leadUnknown: (group: string): string => `They get what ${group} gives access to from their next request.`,
    submit: (fmt: GroupFmt, count: number): string =>
      count === 0 ? 'Add' : `Add ${counted(fmt, count, { one: 'person', other: 'people' })}`,
    submitting: 'Adding…',
    done: 'Done',
    result: (fmt: GroupFmt, added: number, already: number): string =>
      [
        added === 0 ? null : `Added ${counted(fmt, added, { one: 'person', other: 'people' })}.`,
        already === 0
          ? null
          : `${counted(fmt, already, { one: 'was already in the group', other: 'were already in the group' })}.`
      ]
        .filter(Boolean)
        .join(' '),
    /**
     * A refusal the server named; a new reason must be given words here before it compiles.
     * Each reads as a clause after a person's NAME, so none of them may need to agree with it.
     */
    refused: {
      not_found: 'no longer has an account',
      deactivated: 'has been deactivated',
      service: 'is a service account, not a person'
    } satisfies Record<AddMembersResult['refused'][number]['reason'], string>
  },

  remove: {
    title: (name: string, group: string): string => `Remove ${name} from ${group}?`,
    body: (group: string): string =>
      `They stop getting what ${group} gives access to from their next request. Their conversations stay readable, and they can be added again at any time.`,
    submit: 'Remove',
    submitting: 'Removing…'
  },

  removeManager: {
    title: (name: string, group: string): string => `Stop ${name} managing ${group}?`,
    body: (group: string): string =>
      `They can no longer add or remove people, or maintain what ${group} owns. Whether they are in the group does not change.`,
    submit: 'Stop managing',
    submitting: 'Updating…'
  },

  genericError: 'Something went wrong. Try again.',

  deletion: {
    title: (group: string): string => `Delete ${group}?`,
    lead: 'This cannot be undone. Read what changes before you confirm.',
    loading: 'Working out what changes…',
    previewFailed:
      'What deleting it would change could not be worked out, so it cannot be deleted yet. Try again in a moment.',
    members: (fmt: GroupFmt, count: number): string =>
      count === 0
        ? 'Nobody is in it, so nobody loses anything.'
        : `${people(fmt, count)} ${pick(fmt, count, { one: 'stops', other: 'stop' })} getting what it gives access to.`,
    managers: (fmt: GroupFmt, count: number): string =>
      `${counted(fmt, count, { one: 'manager stops', other: 'managers stop' })} managing it.`,
    sharedWith: 'Shared with it',
    /**
     * Four clauses said after an item's name, across a list that mixes feminine knowledge bases
     * with masculine agents and systems in one map. Written to need no agreement in either
     * language rather than to carry a gender the call site cannot supply (PRD-27 D-21).
     */
    fallsBack: 'falls back to only its maintainers',
    staysShared: 'stays shared with its other groups',
    becomesUnusable: 'nobody can use it until an administrator chooses again',
    staysAvailable: 'stays available to its other groups',
    owns: 'Owned by it',
    ownsChoose: 'Owned by it — choose a new owner',
    ownsLead: 'Everything the group owns needs somebody accountable. The new owner’s limits apply.',
    /** Only the kinds that are waiting: "0 sharing requests and …" says nothing a person needs. */
    requests: (fmt: GroupFmt, sharing: number, lending: number): string => {
      const parts = [
        sharing === 0 ? null : counted(fmt, sharing, { one: 'sharing request', other: 'sharing requests' }),
        lending === 0
          ? null
          : counted(fmt, lending, {
              one: 'request to let an agent answer from a base it owns',
              other: 'requests to let an agent answer from bases it owns'
            })
      ].filter((part): part is string => part !== null)
      // No re-casing: every part begins with its own count, so the sentence already starts the
      // way a sentence should. The re-caser this replaced was a no-op on a digit, which is how
      // an assumption about orthography sat here unnoticed (PRD-27 LOC-014).
      return `${fmt.formatList(parts)} will be closed, and the people who asked told.`
    },
    typeName: (group: string): string => `Type ${group} to confirm`,
    submit: 'Delete group',
    submitting: 'Deleting…',
    drifted: 'Something changed while you were reading. The consequences below are up to date — read them again.',
    nothingShared: 'Nothing is shared with it.',
    nothingOwned: 'It owns nothing.'
  },

  // ---------------------------------------------------------------------------
  // A reader's relation to a group
  // ---------------------------------------------------------------------------
  relation: {
    member: 'You are in this group.',
    manager: 'You manage this group but are not in it, so you do not get what is shared with it.',
    member_and_manager: 'You are in this group and you manage it.',
    administrator: 'You are not in this group. As an administrator you can see and change everything about it.',
    outsider: 'You are not in this group.'
  } satisfies Record<GroupRelation, string>,

  /** The owner picker's sentences (`OwnerPicker`, D-19, D-32). */
  ownerPicker: {
    legend: 'Owned by',
    you: (name: string): string => `You (${name})`,
    youHint: 'You maintain it.',
    theyHint: 'They maintain it.',
    groupHint: 'Its managers maintain it.',
    manage: 'you manage',
    anotherGroup: 'Another group',
    anotherPerson: 'Another person',
    groupSearch: 'Find a group',
    groupSearchPlaceholder: 'Search groups by name',
    noGroups: 'No group matches.',
    current: 'current owner'
  }
}
